import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

import { loadGoogleMaps } from '../../utils/googleMapsLoader';

import './GoogleMap.css';
import { GoogleMapProps } from './GoogleMap.types';

/**
 * GoogleMap Component
 *
 * Displays an interactive Google Map with station marker and info window.
 * Handles coordinate validation, map initialization, and responsive loading states.
 * Marker color varies by brand (green for OKKO, orange for others).
 *
 * <GoogleMap
 *   latitude={50.4501}
 *   longitude={30.5234}
 *   stationName="АЗК №1"
 *   stationAddress="Київ, вул. Хрещатик"
 *   brand="ОККО"
 *   height="400px"
 *   zoom={16}
 * />
 */
export const GoogleMap: React.FC<GoogleMapProps> = ({
  latitude,
  longitude,
  stationName = 'АЗК',
  stationAddress = '',
  brand = '',
  height = '300px',
  zoom = 15
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerInstanceRef = useRef<google.maps.Marker | null>(null);
  const infoWindowInstanceRef = useRef<google.maps.InfoWindow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const lat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
  const lng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;

  const hasValidCoordinates = lat && lng && !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;

  useEffect(() => {
    if (!hasValidCoordinates || !mapRef.current) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const initializeMap = async () => {
      try {
        setLoading(true);
        setError(null);

        await loadGoogleMaps();

        if (!mapRef.current || !isMounted) {
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 100));

        if (!isMounted) {
          return;
        }

        const mapInstance = new google.maps.Map(mapRef.current, {
          center: { lat: lat!, lng: lng! },
          zoom: zoom,
          mapTypeId: 'roadmap',
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
          zoomControl: true
        });

        const markerInstance = new google.maps.Marker({
          position: { lat: lat!, lng: lng! },
          map: mapInstance,
          title: `${brand} - ${stationName}`,
          icon: {
            path: `M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z M -5,-30 A 5,5 0 1,0 5,-30 A 5,5 0 1,0 -5,-30 z`,
            scale: 1,
            fillColor: brand?.toUpperCase() === 'ОККО' || brand?.toUpperCase() === 'OKKO' ? '#4caf50' : '#ff9800',
            fillOpacity: 1,
            strokeColor: brand?.toUpperCase() === 'ОККО' || brand?.toUpperCase() === 'OKKO' ? '#4caf50' : '#ff9800',
            strokeWeight: 0,
            anchor: new google.maps.Point(0, 0)
          }
        });

        const infoWindowInstance = new google.maps.InfoWindow({
          headerContent: `${brand} - ${stationName}`,
          content: `<div class="google-map-info-window"><div class="google-map-info-content">${stationAddress}</div></div>`
        });

        markerInstance.addListener('click', () => {
          infoWindowInstance.open(mapInstance, markerInstance);
        });

        google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
          if (isMounted) {
            infoWindowInstance.open(mapInstance, markerInstance);
          }
        });

        mapInstanceRef.current = mapInstance;
        markerInstanceRef.current = markerInstance;
        infoWindowInstanceRef.current = infoWindowInstance;

        if (isMounted) {
          setLoading(false);
        }
      } catch (err) {
        console.error('[GoogleMap] Error initializing:', err);
        if (isMounted) {
          setError('Виникла помилка при завантаженні карти');
          setLoading(false);
        }
      }
    };

    initializeMap();

    return () => {
      isMounted = false;
      if (infoWindowInstanceRef.current) {
        infoWindowInstanceRef.current.close();
      }
      if (markerInstanceRef.current) {
        markerInstanceRef.current.setMap(null);
      }
      mapInstanceRef.current = null;
      markerInstanceRef.current = null;
      infoWindowInstanceRef.current = null;
    };
  }, [lat, lng, hasValidCoordinates, zoom, brand, stationName, stationAddress]);

  if (!hasValidCoordinates) {
    return (
      <Box className="google-map-empty-state" sx={{ height }}>
        <Typography variant="body2">Координати станції відсутні або некоректні</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="google-map-error-state" sx={{ height }}>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className="google-map-container" sx={{ height }}>
      {loading && (
        <Box className="google-map-loading-overlay">
          <CircularProgress size={40} sx={{ color: '#84bd00' }} />
        </Box>
      )}
      <div ref={mapRef} className="google-map-canvas" />
    </Box>
  );
};
